import React, { useContext } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Gift, Link as LinkIcon, MessageSquareText } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import FlexPage from "components/layout/FlexPage";
import Breadcrumbs from "components/common/Breadcrumbs";
import { CurrentUserContext } from "components/context/RequireAuth";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { buildReferralLink } from "components/referrals/utils";
import useTitle from "components/hooks/useTitle";
import Loading from "components/common/Loading";
import CopyTextField from "components/common/CopyTextField";
import { useTrackClientEvent } from "services/clientEvents";
import {
  referralProperties,
  ReferralProperties,
} from "common/memberModuleResolver";

const ReferralModule = ({
  referralProperties,
}: {
  referralProperties: ReferralProperties;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const isMobile = useIsMobile();
  const trackClientEvent = useTrackClientEvent();

  const referralLink = buildReferralLink(
    currentUser.ref,
    referralProperties.code,
    referralProperties.url,
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        width: "100%",
      }}
    >
      {/* Hero section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          padding: isMobile ? "20px" : "28px",
          borderRadius: "16px",
          backgroundColor: "#F2F8F8",
          border: "1px solid #DEEDED",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: "#198282",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Gift size={24} color="white" />
          </Box>
          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <Typography variant="h3" sx={{ color: "#3D3D3D" }}>
              {referralProperties.title}
            </Typography>
            {referralProperties.subtitle && (
              <Typography variant="caption" sx={{ color: "#6B6E7B" }}>
                {referralProperties.subtitle}
              </Typography>
            )}
          </Box>
          {referralProperties.chipLabel && (
            <Chip
              label={referralProperties.chipLabel}
              variant="tealDark"
              sx={{
                fontWeight: 700,
                fontSize: "14px",
                height: "32px",
              }}
            />
          )}
        </Box>
        {referralProperties.description && (
          <Typography variant="body" sx={{ color: "#6B6E7B" }}>
            {referralProperties.description}
          </Typography>
        )}
        {referralProperties.rewards && (
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "12px",
            }}
          >
            {referralProperties.rewards.map((reward) => (
              <Box
                key={reward.label}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  padding: "16px",
                  borderRadius: "12px",
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#198282",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {reward.label}
                </Typography>
                <Typography variant="h2" sx={{ color: "#3D3D3D" }}>
                  {reward.value}
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B6E7B" }}>
                  {reward.caption}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {referralProperties.rewardsFootnote && (
          <Typography variant="caption" sx={{ color: "#6B6E7B" }}>
            {referralProperties.rewardsFootnote}
          </Typography>
        )}
      </Box>

      {/* Link section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <LinkIcon size={18} color="#198282" />
          <Typography variant="bodyHeavy">Share your link</Typography>
        </Box>
        <CopyTextField
          value={referralLink}
          onCopy={() => trackClientEvent("referral-link-copy")}
        />
      </Box>

      {/* Message section */}
      {referralProperties.cannedText && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <MessageSquareText size={18} color="#198282" />
            <Typography variant="bodyHeavy">Or share this message</Typography>
          </Box>
          <CopyTextField
            value={`${referralProperties.cannedText}\n\n${referralLink}`}
            multiline
            onCopy={() => trackClientEvent("referral-message-copy")}
          />
        </Box>
      )}
    </Box>
  );
};

export default () => {
  useTitle("Invite Friends");
  const isMobile = useIsMobile();
  const { data, loading, isDigitalSubscription, isUnpaidSignup, familyStatus } =
    useMemberDetails();
  if (loading || !data) return <Loading />;
  const props = referralProperties(
    { isDigitalSubscription, isUnpaidSignup, familyStatus },
    data?.primaryAdvisor?.profilePath,
  );
  return (
    <FlexPage
      leftAligned
      sx={{
        cursor: props.cursor,
      }}
    >
      <Box sx={{ maxWidth: "600px" }}>
        <Breadcrumbs
          breadcrumbs={[{ name: "Home", link: "/" }, { name: "Invite" }]}
        />
        <Box sx={{ marginTop: isMobile ? "8px" : "12px" }}>
          {props.code ? (
            <ReferralModule referralProperties={props} />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: isMobile ? "20px" : "28px",
                borderRadius: "16px",
                backgroundColor: "#F2F8F8",
                border: "1px solid #DEEDED",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box
                  sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#198282",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Gift size={24} color="white" />
                </Box>
                <Typography variant="h3" sx={{ color: "#3D3D3D" }}>
                  Refer friends, earn rewards
                </Typography>
              </Box>
              <Typography variant="body" sx={{ color: "#6B6E7B" }}>
                After you sign up, you'll get a referral code to share with
                someone who's juggling too much. They'll get $100 off their
                first month — and a serious sigh of relief.
              </Typography>
            </Box>
          )}
        </Box>
        {props.finePrint && (
          <Box
            sx={{
              marginTop: "20px",
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "#FAF9FA",
              border: "1px solid #EAEBEC",
            }}
          >
            {props.finePrint}
          </Box>
        )}
      </Box>
    </FlexPage>
  );
};
